import { Copy, EyeIcon, EyeOffIcon } from 'lucide-react'
import React, { useState } from 'react'
import { toast } from 'sonner'

interface ICHRADepositAccountDetailsProps {
  accountNumber: string;
  routingNumber: string;
  memberSetsUpAutopay: boolean;
  widthClassOverride?: string;
  heightClassOverride?: string;
  roundBorderOverride?: string;
  isActive?: boolean;
}

interface SensitiveNumberProps {
  label: string;
  value: string;
}

const SensitiveNumber: React.FC<SensitiveNumberProps> = ({ label, value }) => {
  const [visible, setVisible] = useState(false)

  const toggleVisibility = () => {
    setVisible(!visible)
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(value).then(
      () => {
        toast.success('Copied to clipboard')
      },
      () => {
        toast.error('Failed to copy')
      }
    )
  }

  const maskedValue = `•••••${value.slice(-3)}`

  return (
    <div className='flex flex-col space-y-1'>
      <div className='flex items-center gap-x-2'>
        <span className='body-sm text-gray-500'>{label}</span>
        <button
          onClick={toggleVisibility}
          className='text-gray-500 hover:text-gray-700 transition-colors'
          aria-label={visible ? 'Hide number' : 'Show number'}
        >
          {visible
            ? <EyeOffIcon size={16} />
            : <EyeIcon size={16} />}
        </button>
      </div>
      <div className='flex items-center gap-x-2'>
        <span className='body sensitive'>{visible ? value : maskedValue}</span>
        <button
          onClick={copyToClipboard}
          className='text-gray-500 hover:text-gray-700 transition-colors'
          aria-label='Copy to clipboard'
        >
          <Copy size={16} />
        </button>
      </div>
    </div>
  )
}

const ICHRADepositAccountDetails: React.FC<ICHRADepositAccountDetailsProps> = ({
  accountNumber,
  routingNumber,
  memberSetsUpAutopay,
  widthClassOverride,
  heightClassOverride,
  roundBorderOverride,
  isActive = true,
}) => {
  // NOTE (as of 8/5/2025) we are currently migrating balances page to transactions page
  // and the overrides are for the V2 styling. However, there is a change that we would move this banner
  // out from the V2 transactions page to somewhere else - so will leave the override here for now.
  // once we deprecate V1 transactions page, we will remove the overrides.
  const widthClass = widthClassOverride ?? 'w-80'
  const heightClass = heightClassOverride ?? 'h-40'
  const roundBorderClass = roundBorderOverride ?? 'rounded-lg'
  const backgroundColor = isActive ? 'bg-white' : 'bg-gray-100'
  return (
    <div className={`flex flex-col ${backgroundColor} ${roundBorderClass} border shadow-sm ${widthClass} ${heightClass}`}>
      <div className='flex flex-1 flex-row justify-between items-center p-4'>
        <SensitiveNumber label='Routing Number' value={routingNumber} />
        <SensitiveNumber label='Account Number' value={accountNumber} />
      </div>
      {memberSetsUpAutopay
        ? (
          <div className='flex-shrink caption border-t p-4 text-gray-800'>
            Use these details to set up ACH autopay for your insurance plan.
          </div>
          )
        : (
          <div className='flex-shrink caption border-t p-4 text-gray-800'>
            Your plan is paid via ACH, but we've already set up autopay for you.
          </div>
          )}
    </div>
  )
}

export default ICHRADepositAccountDetails
